import { Button } from '@components/primitives/Button';
import { Box, Skeleton } from '@mui/material';

import { AppointmentItem } from './AppointmentItem';
import { EmptyState } from './EmptyState';

interface Appointment {
  id: string;
  time: string;
  patientName: string;
  duration: string;
  appointmentType: string;
  isNext: boolean;
  originalAppointment?: {
    encounter?: {
      encounter_id: string;
    };
  };
}

interface AppointmentsListProps {
  appointments: Appointment[] | undefined;
  onAppointmentClick: (appointment: Appointment) => void;
  onSchedule?: () => void;
  onViewSchedule?: () => void;
  loading?: boolean;
}

/**
 * Pure UI component for rendering the appointments list
 * Handles loading, empty, and populated states
 */
export const AppointmentsList: React.FC<AppointmentsListProps> = ({
  appointments,
  onAppointmentClick,
  onSchedule,
  onViewSchedule,
  loading = false,
}) => {
  // Loading state - show skeletons
  if (loading || appointments === undefined) {
    return (
      <>
        {Array.from({ length: 3 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              px: 3,
              py: 2,
              borderTop: index === 0 ? 'none' : '1px solid',
              borderColor: 'borderColors.primary',
              backgroundColor: index === 0 ? 'primary.main' : 'transparent',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton
                variant="text"
                sx={{width: 65, height: 24,
                  bgcolor: index === 0 ? 'rgba(255, 255, 255, 0.3)' : undefined,
                }}
              />
              <Skeleton
                variant="text"
                sx={{width: "60%", height: 24,
                  bgcolor: index === 0 ? 'rgba(255, 255, 255, 0.3)' : undefined,
                }}
              />
              {index === 0 && (
                <Skeleton
                  variant="rectangular"
                  sx={{width: 45, height: 22,
                    ml: 'auto',
                    borderRadius: 1,
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  }}
                />
              )}
            </Box>
          </Box>
        ))}
      </>
    );
  }

  // Empty state
  if (!appointments || appointments.length === 0) {
    return <EmptyState onSchedule={onSchedule} />;
  }

  // Populated state - render appointments
  return (
    <Box>
      {appointments.map((appointment, index) => (
        <AppointmentItem
          key={appointment.id}
          time={appointment.time}
          patientName={appointment.patientName}
          duration={appointment.duration}
          appointmentType={appointment.appointmentType}
          isNext={appointment.isNext}
          isFirst={index === 0}
          onClick={() => onAppointmentClick(appointment)}
        />
      ))}

      {/* View Schedule button at end of list */}
      {onViewSchedule && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'borderColors.primary',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={onViewSchedule}>View Schedule</Button>
        </Box>
      )}
    </Box>
  );
};
